import { r as registerInstance, c as createEvent, h, F as Fragment, g as getElement } from './index-d4808923.js';
import { b as bem } from './index.esm-7182addc.js';
import { b as getLocaleFirstWeekday, c as getTimeZoneOffset, i as isTheSameDate, p as parseToLocaleDate, d as isWithinRange, t as toLocaleISOString, e as decimalToHHMM, I as ISO8601_REGEX, H as HHMMtoDecimal, f as getLocalTimeZone, S as SUPPORTED_TIMEZONES, h as getTimeZoneLabel, j as getDaysInMonth, k as getFirstWeekday } from './date-utils-73271ab4.js';
import { a as chunkArray } from './utils-17533423.js';
import { c as createPopper } from './popper-1604923e.js';

const gravityDatepickerCss = ":host{display:block;height:0;outline:none;position:absolute;z-index:1000}:host([open]){height:auto}.gravity-datepicker{font-feature-settings:\"liga\" on;font-feature-settings:\"liga\" on;-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;background-color:#FFFFFF;border:1px solid rgba(0, 15, 30, 0.05);border-radius:6px;box-shadow:0px 8px 12px 0px rgba(0, 15, 30, 0.0392156863), 0px 2px 4px 0px rgba(0, 15, 30, 0.0196078431);box-sizing:border-box;display:flex;flex-direction:column;gap:8px;opacity:0;padding:12px;transform:scale(0);transform-origin:50% top;transition:opacity 150ms cubic-bezier(0.23, 1, 0.32, 1);user-select:none;visibility:hidden;width:276px}.gravity-datepicker--open{display:flex;height:auto;opacity:1;transform:scale(1);visibility:visible}.gravity-datepicker__header__first{display:flex;justify-content:space-between}.gravity-datepicker__header__first__label{align-items:center;cursor:pointer;display:flex;gap:4px;margin:0}.gravity-datepicker__header__second{display:none}.gravity-datepicker__header__second--open{display:flex;gap:4px;padding:8px 0}.gravity-datepicker__header__buttons{display:flex;gap:4px}.gravity-datepicker__header__button{align-items:center;background:none;border:0;cursor:pointer;display:inline-flex;justify-content:center;padding:0}.gravity-datepicker__select{cursor:pointer;display:inline-flex;flex-grow:1;position:relative;text-decoration:none}.gravity-datepicker__select__icon{height:18px;pointer-events:none;position:absolute;right:12px;top:50%;transform:translateY(-50%);width:18px}.gravity-datepicker__select__input{width:100%}.gravity-datepicker__select__input__timezone{width:100%}.gravity-datepicker__year-input{width:60px}.gravity-datepicker__calendar__table{border-collapse:collapse;border-spacing:0;text-align:center;width:100%}.gravity-datepicker .day{cursor:pointer;position:relative}.gravity-datepicker .day::after{border-radius:9999px;content:\"\";font-size:0;height:100%;left:50%;position:absolute;top:50%;transform:translate(-50%, -50%);width:100%}.gravity-datepicker .day:active{outline:none}.gravity-datepicker .day__text{display:block;margin:0 auto;position:relative;text-align:center;width:20px;z-index:1}.gravity-datepicker .day--enabled:hover{outline:none}.gravity-datepicker .day--enabled:hover::after{background-color:rgba(0, 15, 30, 0.05)}.gravity-datepicker .day--current{outline:none}.gravity-datepicker .day--current::after{box-shadow:inset 0 0 0 1px rgba(0, 15, 30, 0.2)}.gravity-datepicker .day--selected{outline:none}.gravity-datepicker .day--selected::after{background-color:#1B6AEE}.gravity-datepicker .day--selected:has(+.day--between){background-color:#DDE9FC;border-radius:9999px 0 0 9999px}.gravity-datepicker .day--selected:hover::after{background-color:#175ACA}.gravity-datepicker .day--between{background-color:#DDE9FC}.gravity-datepicker .day--between:last-child{border-radius:0 2px 2px 0}.gravity-datepicker .day--between:first-child{border-radius:2px 0 0 2px}.gravity-datepicker .day--between+.day--selected{background-color:#DDE9FC;border-radius:0 9999px 9999px 0}.gravity-datepicker .day,.gravity-datepicker .weekday{height:34px;padding:0;width:34px}.gravity-datepicker .week{border-bottom:2px solid transparent}.gravity-datepicker__footer{border-top:1px solid rgba(0, 15, 30, 0.05);display:flex;flex-direction:column;gap:8px;padding-top:12px}.gravity-datepicker--required .gravity-datepicker__input{border-color:#FF9000}.gravity-datepicker--readonly .gravity-datepicker__input,.gravity-datepicker--readonly .gravity-datepicker__input:hover,.gravity-datepicker--disabled .gravity-datepicker__input,.gravity-datepicker--disabled .gravity-datepicker__input:hover{background-color:#F8F8F8;border-color:rgba(0, 15, 30, 0.1);cursor:default}";
const GravityDatepickerStyle0 = gravityDatepickerCss;

const generateClassNames = bem('gravity-datepicker');
const GravityDatepicker = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.isOpen = createEvent(this, "isOpen", 1);
        this.selected = createEvent(this, "selected", 5);
        this.timezoneChanged = createEvent(this, "timezoneChanged", 5);
        this.weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
        this.monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        this.today = new Date();
        // define with which day should the week start, sunday: 0, monday: 1, etc.
        this.firstWeekday = getLocaleFirstWeekday();
        // shift the weekdays according to the first day
        this.shiftedWeekDays = [
            ...this.weekDays.slice(this.firstWeekday, this.weekDays.length),
            ...this.weekDays.slice(0, this.firstWeekday),
        ];
        this.popperInstance = undefined;
        this.handleMonthSelect = (event) => {
            const detail = event.detail[0];
            this.setCurrentDate(this.currentYear, parseInt(detail));
        };
        this.handleTimeZoneSelect = (event) => {
            var _a;
            const detail = event.detail[0];
            const oldTimeZoneOffset = getTimeZoneOffset(this.timezone, this.selectedDate);
            this.timezone = detail;
            if (this.selectedDate) {
                this.selectedDate.setMinutes(this.selectedDate.getMinutes() +
                    oldTimeZoneOffset * 60 -
                    getTimeZoneOffset(this.timezone, this.selectedDate) * 60);
                (_a = this.secondSelectedDate) === null || _a === void 0 ? void 0 : _a.setMinutes(this.secondSelectedDate.getMinutes() +
                    oldTimeZoneOffset * 60 -
                    getTimeZoneOffset(this.timezone, this.secondSelectedDate) * 60);
                this.value = this.getSelectedISOString();
                this.selected.emit(this.value);
            }
            this.timezoneChanged.emit(this.timezone);
            this.selected.emit(this.value);
        };
        this.handleYearInput = (event) => {
            const target = event.target;
            const inputValue = target.value.trim();
            if (inputValue.length === 4) {
                if (this.isValidYear(inputValue)) {
                    this.setCurrentDate(parseInt(inputValue, 10), this.currentMonth);
                }
                else {
                    this.resetYearInput(target);
                }
            }
            else if (inputValue.length > 4) {
                this.resetYearInput(target);
            }
        };
        this.handleYearBlur = (event) => {
            const target = event.target;
            const inputValue = target.value.trim();
            if (!this.isValidYear(inputValue)) {
                this.resetYearInput(target);
            }
        };
        this.handleSelectBlur = (event) => {
            this.element.focus();
            event.preventDefault();
            event.stopPropagation();
        };
        this.handleSelectOpen = (event) => {
            const isOpen = event.detail;
            const selectElement = event.target;
            if (isOpen) {
                selectElement.style.zIndex = '2';
            }
            else {
                selectElement.style.zIndex = '';
            }
        };
        this.deriveTimezoneFromValue = true;
        this.disabled = false;
        this.for = '';
        this.maxDate = undefined;
        this.minDate = undefined;
        this.offset = 0;
        this.open = false;
        this.position = 'center';
        this.positionStrategy = 'absolute';
        this.timezone = undefined;
        this.timezoneSelect = true;
        this.type = 'date';
        this.value = undefined;
        this.currentMonth = undefined;
        this.currentYear = undefined;
        this.headerExpanded = false;
        this.secondSelectedDate = undefined;
        this.secondSelectedTime = undefined;
        this.selectedDate = undefined;
        this.selectedTime = undefined;
    }
    valueHandler(newValue) {
        if (newValue) {
            this.setSelectedValue();
        }
        else {
            this.clearSelection();
        }
    }
    updateOpen() {
        if (this.open) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    /**
     * Open the datepicker
     */
    async openDatepicker() {
        this.show();
    }
    /**
     * Close the datepicker
     */
    async closeDatepicker() {
        this.hide();
    }
    setListeners() {
        if (!this.anchorElement) {
            return;
        }
        this.anchorElement.onclick = () => {
            if (this.open) {
                this.hide();
            }
            else if (!this.disabled) {
                this.show();
            }
        };
        this.element.onblur = (event) => {
            if (event.relatedTarget == this.anchorElement) {
                return;
            }
            this.hide();
        };
    }
    validateValue() {
        if (!this.value) {
            return;
        }
        const minDate = new Date(this.minDate);
        const maxDate = new Date(this.maxDate);
        if (this.minDate || this.maxDate) {
            if (this.value.split('/').length > 1) {
                const firstDate = new Date(this.value.split('/')[0]);
                const secondDate = new Date(this.value.split('/')[1]);
                if (firstDate < minDate || firstDate > maxDate || secondDate < minDate || secondDate > maxDate) {
                    this.value = '';
                    return;
                }
            }
            else {
                const date = new Date(this.value);
                if (date < minDate || date > maxDate) {
                    this.value = '';
                    return;
                }
            }
        }
    }
    hasSameTimeZoneOffset(timezoneA, timezoneB, date) {
        return getTimeZoneOffset(timezoneA, date) == getTimeZoneOffset(timezoneB, date);
    }
    setSelectedValue() {
        var _a;
        if (!this.value) {
            return;
        }
        if (this.value.split('/').length > 1) {
            this.selectedDate = new Date(this.value.split('/')[0]);
            this.secondSelectedDate = new Date(this.value.split('/')[1]);
            const timeZoneString = this.getTimeZoneFromISOString(this.value.split('/')[0]);
            if (!this.hasSameTimeZoneOffset(this.timezone, timeZoneString, this.selectedDate) &&
                this.deriveTimezoneFromValue) {
                this.timezone = timeZoneString;
                this.timezoneChanged.emit(this.timezone);
            }
        }
        else {
            this.selectedDate = new Date(this.value);
            const timeZoneString = this.getTimeZoneFromISOString(this.value.split('/')[0]);
            if (!this.hasSameTimeZoneOffset(this.timezone, timeZoneString, this.selectedDate) &&
                this.deriveTimezoneFromValue) {
                this.timezone = timeZoneString;
                this.timezoneChanged.emit(this.timezone);
            }
        }
        const localeDate = (_a = this.selectedDate) === null || _a === void 0 ? void 0 : _a.toLocaleDateString('en-US', { timeZone: this.timezone });
        const month = localeDate.split('/')[0];
        const year = localeDate.split('/')[2];
        this.setCurrentDate(parseInt(year), parseInt(month) - 1);
    }
    isToday(year, month, day) {
        const date = new Date(year, month, day);
        return isTheSameDate(date, this.today);
    }
    clearSelection() {
        this.selectedDate = undefined;
        this.secondSelectedDate = undefined;
        this.value = this.getSelectedISOString();
    }
    adjustCurrentDateToMinMax() {
        if (!this.minDate && !this.maxDate) {
            return;
        }
        if (this.minDate) {
            const minDate = parseToLocaleDate(this.minDate);
            this.setCurrentDate(minDate.getFullYear(), minDate.getMonth());
        }
        else if (this.maxDate) {
            const maxDate = parseToLocaleDate(this.maxDate);
            if (maxDate < this.today) {
                this.setCurrentDate(maxDate.getFullYear(), maxDate.getMonth());
            }
        }
    }
    setCurrentDate(year, month) {
        const date = new Date(year, month, 1);
        this.currentMonth = date.getMonth();
        this.currentYear = date.getFullYear();
    }
    isSelected(year, month, day) {
        var _a, _b;
        const selectedDateString = (_a = this.selectedDate) === null || _a === void 0 ? void 0 : _a.toLocaleDateString('en-US', { timeZone: this.timezone });
        const secondSelectedDateString = (_b = this.secondSelectedDate) === null || _b === void 0 ? void 0 : _b.toLocaleDateString('en-US', { timeZone: this.timezone });
        const dateString = `${month + 1}/${day}/${year}`;
        return dateString == selectedDateString || dateString == secondSelectedDateString;
    }
    isDisabled(year, month, day) {
        if (!this.minDate && !this.maxDate) {
            return false;
        }
        const date = new Date(Date.UTC(year, month, day));
        date.setUTCHours(-getTimeZoneOffset(this.timezone, date));
        const minDate = new Date(this.minDate);
        const maxDate = new Date(this.maxDate);
        // Check if the date is equal to the minDate or maxDate
        if (isTheSameDate(date, minDate) || isTheSameDate(date, maxDate)) {
            return false;
        }
        return date < minDate || date > maxDate;
    }
    getDayTextColor(year, month, day) {
        if (this.isDisabled(year, month, day)) {
            return 'dark-muted';
        }
        else if (this.isSelected(year, month, day)) {
            return 'light';
        }
        else if (this.isToday(year, month, day)) {
            return 'dark';
        }
        else {
            return 'dark';
        }
    }
    isBetweenSelectedDates(year, month, day) {
        if (this.selectedDate && this.secondSelectedDate) {
            const date = new Date(Date.UTC(year, month, day));
            date.setUTCHours(-getTimeZoneOffset(this.timezone, date));
            // copy dates without hours/minutes
            const selectedDate = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
            const secondSelectedDate = new Date(this.secondSelectedDate.getFullYear(), this.secondSelectedDate.getMonth(), this.secondSelectedDate.getDate());
            if ((date > selectedDate && date < secondSelectedDate) || (date > secondSelectedDate && date < selectedDate)) {
                return true;
            }
        }
        else {
            return false;
        }
    }
    handleDateSelect(year, month, day) {
        if (this.isDisabled(year, month, day)) {
            return;
        }
        const date = new Date(Date.UTC(year, month, day, 0, 0));
        if ((this.type == 'daterange' || this.type == 'datetimerange') &&
            this.selectedDate &&
            date > this.selectedDate &&
            (!this.secondSelectedDate ||
                (isTheSameDate(this.selectedDate, this.secondSelectedDate) && this.type == 'datetimerange'))) {
            this.setTimeAccordingToSecondSelectedTime(date);
            this.secondSelectedDate = date;
        }
        else if (this.type == 'datetimerange') {
            const secondDate = new Date(date.toISOString());
            this.setTimeAccordingToSelectedTime(date);
            this.selectedDate = date;
            this.setTimeAccordingToSecondSelectedTime(secondDate);
            this.secondSelectedDate = secondDate;
        }
        else {
            this.setTimeAccordingToSelectedTime(date);
            this.selectedDate = date;
            this.secondSelectedDate = undefined;
        }
        // Adjust selected date to minDate if necessary
        if (!isWithinRange(this.selectedDate, this.minDate, this.maxDate)) {
            this.selectedDate = this.minDate ? new Date(this.minDate) : this.selectedDate;
        }
        this.value = this.getSelectedISOString();
        this.selected.emit(this.value);
        // close datepicker when only a single date is selected
        if (this.type == 'date') {
            this.hide();
        }
    }
    setTimeAccordingToSelectedTime(date) {
        if (this.selectedDate) {
            const localTime = this.selectedDate.toLocaleTimeString('en-US', { timeZone: this.timezone, hour12: false });
            const localHour = parseInt(localTime.split(':')[0]);
            date.setUTCHours(localHour - getTimeZoneOffset(this.timezone, date));
            date.setUTCMinutes(this.selectedDate.getUTCMinutes());
        }
        else if (this.selectedTime) {
            date.setUTCHours(this.selectedTime.getUTCHours());
            date.setUTCMinutes(this.selectedTime.getUTCMinutes());
        }
        else {
            date.setUTCHours(-getTimeZoneOffset(this.timezone, date));
        }
    }
    setTimeAccordingToSecondSelectedTime(date) {
        if (this.secondSelectedDate) {
            const localTime = this.secondSelectedDate.toLocaleTimeString('en-US', { timeZone: this.timezone, hour12: false });
            const localHour = parseInt(localTime.split(':')[0]);
            date.setUTCHours(localHour - getTimeZoneOffset(this.timezone, date));
            date.setUTCMinutes(this.secondSelectedDate.getUTCMinutes());
        }
        else if (this.secondSelectedTime) {
            date.setUTCHours(this.secondSelectedTime.getUTCHours());
            date.setUTCMinutes(this.secondSelectedTime.getUTCMinutes());
        }
        else {
            date.setUTCHours(-getTimeZoneOffset(this.timezone, date));
        }
    }
    isValidYear(inputValue) {
        const year = parseInt(inputValue, 10);
        return (inputValue.length === 4 &&
            !isNaN(year) &&
            year >= 1900 &&
            (inputValue.startsWith('1') || inputValue.startsWith('2')));
    }
    resetYearInput(target) {
        const currentYear = this.today.getFullYear().toString();
        target.value = currentYear;
        this.setCurrentDate(parseInt(currentYear, 10), this.currentMonth);
    }
    handleTimeSelect(time) {
        if (isNaN(time.getTime())) {
            this.selectedDate = undefined;
            this.value = this.getSelectedISOString();
            this.selected.emit(this.value);
            return;
        }
        this.selectedTime = time;
        if (this.selectedDate) {
            this.selectedDate.setUTCHours(time.getUTCHours());
            this.selectedDate.setUTCMinutes(time.getUTCMinutes());
            this.selectedDate.setUTCDate(time.getUTCDate());
            this.selectedDate.setUTCMonth(time.getUTCMonth());
            this.selectedDate.setUTCFullYear(time.getUTCFullYear());
            this.value = this.getSelectedISOString();
            this.selected.emit(this.value);
        }
        else if (this.type === 'time') {
            this.selectedDate = this.selectedTime;
            this.value = this.getSelectedISOString();
            this.selected.emit(this.value);
        }
    }
    handleSecondTimeSelect(time) {
        if (isNaN(time.getTime())) {
            this.secondSelectedDate = undefined;
            this.value = this.getSelectedISOString();
            this.selected.emit(this.value);
            return;
        }
        this.secondSelectedTime = time;
        if (this.selectedDate && !this.secondSelectedDate) {
            this.secondSelectedDate = new Date(this.selectedDate);
        }
        else if (this.secondSelectedDate) {
            this.secondSelectedDate.setUTCHours(time.getUTCHours());
            this.secondSelectedDate.setUTCMinutes(time.getUTCMinutes());
            this.secondSelectedDate.setUTCDate(time.getUTCDate());
            this.secondSelectedDate.setUTCMonth(time.getUTCMonth());
            this.secondSelectedDate.setUTCFullYear(time.getUTCFullYear());
            this.value = this.getSelectedISOString();
            this.selected.emit(this.value);
        }
    }
    getSelectedISOString() {
        let string = '';
        if (this.selectedDate) {
            const timeZoneOffset = getTimeZoneOffset(this.timezone, this.selectedDate);
            // if we have time input we always give UTC time back
            if (this.type == 'time') {
                string = toLocaleISOString(this.selectedDate, timeZoneOffset * 60);
            }
            else {
                if (this.deriveTimezoneFromValue) {
                    string = toLocaleISOString(this.selectedDate, timeZoneOffset * 60).replace('Z', decimalToHHMM(timeZoneOffset));
                }
                else {
                    string = this.selectedDate.toISOString();
                }
                if (this.secondSelectedDate) {
                    if (this.deriveTimezoneFromValue) {
                        string +=
                            '/' +
                                toLocaleISOString(this.secondSelectedDate, timeZoneOffset * 60).replace('Z', decimalToHHMM(timeZoneOffset));
                    }
                    else {
                        string += '/' + this.secondSelectedDate.toISOString();
                    }
                }
            }
        }
        return string;
    }
    getTimeZoneFromISOString(date) {
        // iso dates doesn't contain timezone information just the offsets
        const matchArr = date.match(ISO8601_REGEX);
        if (matchArr === null || matchArr === void 0 ? void 0 : matchArr[7]) {
            const timeOffsetString = matchArr[7];
            if (timeOffsetString === 'Z') {
                return 'Atlantic/Reykjavik';
            }
            const offset = HHMMtoDecimal(timeOffsetString);
            const dateString = new Date(date);
            // if offset is the same as our locale then display our locale
            if (offset == getTimeZoneOffset(getLocalTimeZone(), dateString)) {
                return getLocalTimeZone();
            }
            else {
                const timezone = SUPPORTED_TIMEZONES.find((timeZone) => getTimeZoneOffset(timeZone, dateString) == offset);
                if (timezone) {
                    return timezone;
                }
                return 'Atlantic/Reykjavik';
            }
        }
        else {
            // no offset found we assume utc time
            return 'Atlantic/Reykjavik';
        }
    }
    computeTimezoneOptions() {
        const timeZoneOptions = [];
        const selectedTimeZone = [];
        SUPPORTED_TIMEZONES.forEach((timeZone) => {
            timeZoneOptions.push({
                id: timeZone,
                label: getTimeZoneLabel(timeZone),
            });
        });
        if (this.timezone && SUPPORTED_TIMEZONES.includes(this.timezone)) {
            selectedTimeZone.push(this.timezone);
        }
        return {
            options: timeZoneOptions,
            selected: selectedTimeZone,
        };
    }
    computeMonthsOptions() {
        const monthOptions = [];
        const selectedMonths = [];
        this.monthNames.forEach((name, index) => {
            monthOptions.push({
                id: index.toString(),
                label: name,
            });
            if (this.currentMonth === index) {
                selectedMonths.push(index.toString());
            }
        });
        return {
            options: monthOptions,
            selected: selectedMonths,
        };
    }
    renderTimeOrDate() {
        var _a;
        if (this.type == 'time') {
            return (h("div", { class: generateClassNames('header') }, h("gravity-time-input", { class: generateClassNames('time-input'), time: (_a = this.selectedDate) !== null && _a !== void 0 ? _a : this.selectedTime, maxDate: this.maxDate, minDate: this.minDate, onTimeChanged: (event) => this.handleTimeSelect(event.detail) })));
        }
        return this.renderDatePickerCalendar();
    }
    renderTimezoneSelect() {
        if (!this.timezoneSelect) {
            return;
        }
        return (h("div", { class: generateClassNames('select') }, h("gravity-select", { class: generateClassNames('select__input__timezone'), clearable: false, options: this.computeTimezoneOptions().options, selected: this.computeTimezoneOptions().selected, onOptionsSelected: this.handleTimeZoneSelect, size: "small", queryType: "filter", onBlur: this.handleSelectBlur, ref: (el) => (this.timezoneSelectElement = el) })));
    }
    renderDatePickerCalendar() {
        var _a, _b, _c;
        const visibleDays = [];
        let visibleChunkedDays = [];
        const daysInLastMonth = getDaysInMonth(this.currentMonth - 1, this.currentYear);
        const firstWeekdayInCurrentMonth = getFirstWeekday(this.currentMonth, this.currentYear);
        const daysInCurrentMonth = getDaysInMonth(this.currentMonth, this.currentYear);
        const difference = firstWeekdayInCurrentMonth <= this.firstWeekday
            ? 7 - Math.abs(this.firstWeekday - firstWeekdayInCurrentMonth)
            : Math.abs(this.firstWeekday - firstWeekdayInCurrentMonth);
        for (let i = daysInLastMonth + 1 - difference; i <= daysInLastMonth; i++) {
            const isSelected = this.isSelected(this.currentYear, this.currentMonth - 1, i);
            const isBetween = !isSelected ? this.isBetweenSelectedDates(this.currentYear, this.currentMonth - 1, i) : false;
            visibleDays.push(h("td", { class: `
        day
        ${isSelected ? 'day--selected' : ''}
        ${isBetween ? 'day--between' : ''}
        ${!this.isDisabled(this.currentYear, this.currentMonth - 1, i) ? 'day--enabled' : ''}
        ${this.isToday(this.currentYear, this.currentMonth - 1, i) ? 'day--current' : ''}
        `, onClick: () => {
                    this.handleDateSelect(this.currentYear, this.currentMonth - 1, i);
                }, tabIndex: 0 }, h("gravity-text", { class: "day__text", size: "xx-small", color: isSelected ? 'light' : 'dark-muted', weight: "medium" }, i)));
        }
        // fill the array with the actual dates
        for (let i = 1; i <= daysInCurrentMonth; i++) {
            const isSelected = this.isSelected(this.currentYear, this.currentMonth, i);
            const isBetween = !isSelected ? this.isBetweenSelectedDates(this.currentYear, this.currentMonth, i) : false;
            visibleDays.push(h("td", { class: `
            day
            ${isSelected ? 'day--selected' : ''}
            ${isBetween ? 'day--between' : ''}
            ${!this.isDisabled(this.currentYear, this.currentMonth, i) ? 'day--enabled' : ''}
            ${this.isToday(this.currentYear, this.currentMonth, i) ? 'day--current' : ''}
          `, onClick: () => this.handleDateSelect(this.currentYear, this.currentMonth, i), tabIndex: 0 }, h("gravity-text", { class: "day__text", size: "xx-small", color: this.getDayTextColor(this.currentYear, this.currentMonth, i), weight: "medium" }, i)));
        }
        // fill the array till it can be divided with 7
        for (let i = 1; visibleDays.length % 7 != 0; i++) {
            const isSelected = this.isSelected(this.currentYear, this.currentMonth + 1, i);
            const isBetween = !isSelected ? this.isBetweenSelectedDates(this.currentYear, this.currentMonth + 1, i) : false;
            visibleDays.push(h("td", { class: `
            day
            ${isSelected ? 'day--selected' : ''}
            ${isBetween ? 'day--between' : ''}
            ${!this.isDisabled(this.currentYear, this.currentMonth + 1, i) ? 'day--enabled' : ''}
            ${this.isToday(this.currentYear, this.currentMonth + 1, i) ? 'day--current' : ''}
          `, onClick: () => {
                    this.handleDateSelect(this.currentYear, this.currentMonth + 1, i);
                }, tabIndex: 0 }, h("gravity-text", { class: "day__text", size: "xx-small", color: this.isSelected(this.currentYear, this.currentMonth + 1, i) ? 'light' : 'dark-muted', weight: "medium" }, i)));
        }
        // split days into weeks (rows)
        visibleChunkedDays = chunkArray(visibleDays, 7);
        return [
            h(Fragment, null, h("div", { class: generateClassNames('header') }, h("div", { class: generateClassNames('header__first') }, h("div", null, h("label", { class: generateClassNames('header__first__label'), onClick: () => (this.headerExpanded = !this.headerExpanded) }, h("gravity-text", { size: "xx-small", weight: "medium", color: "dark" }, this.monthNames[this.currentMonth], " ", this.currentYear), h("gravity-icon", { name: this.headerExpanded ? 'arrow-up' : 'arrow-down', size: "tiny", color: "dark" }))), h("div", { class: generateClassNames('header__buttons') }, h("button", { class: generateClassNames('header__button'), onClick: () => this.setCurrentDate(this.currentYear, this.currentMonth - 1) }, h("gravity-icon", { name: "arrow-left-outline", size: "xxx-small", color: "dark" })), h("button", { class: generateClassNames('header__button'), onClick: () => this.setCurrentDate(this.currentYear, this.currentMonth + 1) }, h("gravity-icon", { name: "arrow-right-outline", size: "xxx-small", color: "dark" })))), h("div", { class: generateClassNames('header__second', {
                    open: this.headerExpanded,
                }) }, h("div", { class: generateClassNames('select') }, h("gravity-select", { class: generateClassNames('select__input'), clearable: false, options: this.computeMonthsOptions().options, selected: this.computeMonthsOptions().selected, onOptionsSelected: this.handleMonthSelect, size: "small", ref: (el) => (this.monthSelectElement = el), onIsOpen: this.handleSelectOpen })), h("gravity-input", { class: generateClassNames('year-input'), type: "number", value: this.currentYear.toString(), onInput: this.handleYearInput, onBlur: this.handleYearBlur, clearable: false, size: "small" }))), h("div", { class: generateClassNames('calendar') }, h("table", { class: generateClassNames('calendar__table') }, h("thead", null, h("tr", null, this.shiftedWeekDays.map((weekDay) => {
                return (h("th", { class: "weekday" }, h("gravity-text", { size: "xx-small", color: "dark", weight: "medium" }, weekDay)));
            }))), h("tbody", null, visibleChunkedDays.map((week) => {
                return h("tr", { class: "week" }, week.map((day) => day));
            }))))),
            (this.type == 'datetime' || this.type == 'datetimerange') && (h("div", { class: generateClassNames('footer') }, this.open && this.renderTimezoneSelect(), this.type == 'datetimerange' && [
                h("gravity-time-input", { class: generateClassNames('time-input'), time: (_a = this.selectedDate) !== null && _a !== void 0 ? _a : this.selectedTime, "min-date": this.minDate, "max-date": this.maxDate, "time-zone": this.timezone, label: 'Start: ' + this.dateToStartDate(this.selectedDate), onTimeChanged: (event) => this.handleTimeSelect(event.detail) }),
                h("gravity-time-input", { class: generateClassNames('time-input'), time: (_b = this.secondSelectedDate) !== null && _b !== void 0 ? _b : this.secondSelectedTime, "min-date": this.minDate, "max-date": this.maxDate, "time-zone": this.timezone, label: 'End: ' + this.dateToStartDate(this.secondSelectedDate), onTimeChanged: (event) => this.handleSecondTimeSelect(event.detail) }),
            ], this.type == 'datetime' && (h("gravity-time-input", { class: generateClassNames('time-input'), time: (_c = this.selectedDate) !== null && _c !== void 0 ? _c : this.selectedTime, "min-date": this.minDate, "max-date": this.maxDate, "time-zone": this.timezone, onTimeChanged: (event) => this.handleTimeSelect(event.detail) })))),
        ];
    }
    dateToStartDate(date) {
        if (!date) {
            return '';
        }
        return toLocaleISOString(date, getTimeZoneOffset(this.timezone, date) * 60).split('T')[0];
    }
    show() {
        var _a, _b;
        if (this.open) {
            this.createPopUp();
            return;
        }
        this.open = true;
        this.isOpen.emit(this.open);
        this.element.tabIndex = 0;
        this.element.focus();
        (_a = this.popperInstance) === null || _a === void 0 ? void 0 : _a.forceUpdate();
        (_b = this.popperInstance) === null || _b === void 0 ? void 0 : _b.update();
    }
    hide() {
        var _a, _b;
        if (!this.open) {
            return;
        }
        this.open = false;
        this.isOpen.emit(this.open);
        (_a = this.popperInstance) === null || _a === void 0 ? void 0 : _a.forceUpdate();
        (_b = this.popperInstance) === null || _b === void 0 ? void 0 : _b.update();
    }
    createPopUp() {
        if (!this.anchorElement) {
            return;
        }
        let placement = 'bottom';
        let flipModifier = {
            name: 'flip',
            options: {
                fallbackPlacements: ['bottom-start', 'bottom-end', 'bottom'],
            },
        };
        const offset = [0, -this.offset + 12];
        switch (this.position) {
            case 'right':
                placement = 'bottom-end';
                flipModifier = {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['bottom-start', 'top-end', 'top-start'],
                    },
                };
                break;
            case 'left':
                placement = 'bottom-start';
                flipModifier = {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
                    },
                };
                break;
            case 'center':
                placement = 'bottom';
                flipModifier = {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['top', 'bottom-start', 'top-start', 'bottom-end', 'top-end'],
                    },
                };
                break;
        }
        this.popperInstance = createPopper(this.anchorElement, this.element, {
            strategy: this.positionStrategy,
            placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset,
                    },
                },
                Object.assign({}, flipModifier),
            ],
        });
    }
    componentWillLoad() {
        var _a;
        this.currentMonth = this.today.getMonth();
        this.currentYear = this.today.getFullYear();
        if (!this.timezone) {
            this.timezone = getLocalTimeZone();
            this.timezoneChanged.emit(this.timezone);
        }
        this.adjustCurrentDateToMinMax();
        this.validateValue();
        this.setSelectedValue();
        this.anchorElement = this.for
            ? ((_a = this.element.parentNode) === null || _a === void 0 ? void 0 : _a.querySelector(`#${this.for}`)) || this.element.parentElement
            : this.element.parentElement;
        if (this.open) {
            this.show();
        }
    }
    componentDidLoad() {
        this.setListeners();
        if (this.open) {
            this.createPopUp();
        }
        this.element.onkeydown = (event) => {
            if (event.key == 'Escape') {
                event.stopPropagation();
                this.hide();
            }
            if (!this.element.shadowRoot.activeElement) {
                return;
            }
            if (event.key == 'Enter' && this.element.shadowRoot.activeElement.classList.contains('day')) {
                const day = this.element.shadowRoot.activeElement;
                day.click();
            }
        };
    }
    componentDidRender() {
        var _a;
        if (this.popperInstance) {
            (_a = this.popperInstance) === null || _a === void 0 ? void 0 : _a.destroy();
            this.createPopUp();
        }
    }
    disconnectedCallback() {
        var _a;
        (_a = this.popperInstance) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    render() {
        return (h("div", { key: '1ab823e902b93b7689bbac65dfd02cd64af040ac', class: generateClassNames({
                open: this.open,
            }) }, this.renderTimeOrDate()));
    }
    get element() { return getElement(this); }
    static get watchers() { return {
        "value": ["valueHandler"],
        "open": ["updateOpen"]
    }; }
};
GravityDatepicker.style = GravityDatepickerStyle0;

export { GravityDatepicker as gravity_datepicker };

//# sourceMappingURL=gravity-datepicker.entry.js.map